import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import styles from "./AgentLive.module.css";
import WaitingForCallModal from "./WaitingForCallModal";
import AnimatedButton from "../../AnimatedButton/AnimatedButton";
import { useAgentStore } from "../../../Store/agentDetailStore";
import { fetchAgentDetailById, getAgentCalls } from "../../../Store/apiStore";
import { markSetupStep } from "../../../lib/setupScore";
import { trackEvent } from "../../../lib/analytics";
import { useGlobalCall } from "../../../contexts/GlobalCallContext";
import { useBflowStore } from "../../../Store/bflowStore";
import useBackTo from "../../../lib/useBackTo";
import {
    armPostCallGate,
    markDialerOpened,
    clearPendingDial,
    getPendingDial,
    findCallAfter,
    routeForCall,
} from "../../../lib/bflowPostCall";

/*
 * AgentLive — Variant B, screen 3.
 *
 *   /fast-step → /receptionist-number → /agent-live   ← YE SCREEN
 *
 * Number assign ho chuka hai. Ab user ko apne HI phone se agent ko call karke
 * dekhna hai. Us call ke baad wo do me se ek screen pe jaata hai:
 *
 *   call >= 30 sec → /call-summary   (Sophia ne kya capture kiya + transcript)
 *   call <  30 sec → /21-day-trial
 *
 * Call app ke BAHAR hoti hai, isliye "dial kiya tha" ka nishaan localStorage me
 * jaata hai (lib/bflowPostCall.js) — user app background me daale ya KILL kar
 * de, wapas aane par gate phir bhi chalta hai.
 *
 * GoLiveCelebration (variant A ki celebration screen) se alag hai: wo paid,
 * poori onboarding ke baad wali screen hai. Dono ko ek component me ghusane se
 * A/B dono ganda ho jaata.
 */

// voip_numbers ke teeno shapes (array / JSON string / plain string) handle karo.
const parseVoipNumber = (raw) => {
    if (!raw) return "";
    if (Array.isArray(raw)) return raw[0] || "";
    if (typeof raw === "string") {
        if (raw.trim().startsWith("[")) {
            try {
                const parsed = JSON.parse(raw);
                return Array.isArray(parsed) ? parsed[0] || "" : parsed || "";
            } catch {
                return raw;
            }
        }
        return raw;
    }
    return "";
};

// tel: link ke liye — sirf digits aur leading +.
const toTelHref = (num) => `tel:${String(num).replace(/[^\d+]/g, "")}`;

// Main card me US business number ko readable format me dikhao. Original value
// copy aur tel: action ke liye waise hi rehta hai.
const formatNumberForDisplay = (num) => {
    const digits = String(num || "").replace(/\D/g, "");
    const nationalNumber =
        digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;

    if (nationalNumber.length === 10) {
        return `+1 ${nationalNumber.slice(0, 3)}-${nationalNumber.slice(3, 6)}-${nationalNumber.slice(6)}`;
    }

    return num;
};

// "Continue with this number" se aane ka nishaan — ReceptionistNumber set karta
// hai, ye screen consume karke confetti chalati hai. Export isliye ki dono
// jagah ek hi key rahe.
export const CELEBRATE_KEY = "bflow_agent_live_celebrate";

const AgentLive = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { agentData } = useAgentStore();
    // Call yahan bhi live ho sakti hai (web call). Agent ko batao ki user ab
    // aakhri screen pe hai aur uska number assign ho chuka hai.
    const { injectContext, callStatus } = useGlobalCall();
    const introInjectedRef = useRef(false);
    // Socket (ya app-kill fallback) yahan detected call rakhta hai. Isi ke aane
    // par screen "Call captured" card + Activate CTA me badal jaati hai.
    const capturedCall = useBflowStore((s) => s.capturedCall);
    const setCapturedCall = useBflowStore((s) => s.setCapturedCall);
    // call_started aa chuka hai par call abhi khatam nahi — modal "Call in
    // progress" dikhata hai aur uska timeout ruk jaata hai.
    const callInProgress = useBflowStore((s) => s.callInProgress);

    const [agentDetails, setAgentDetails] = useState(null);
    const [businessDetails, setBusinessDetails] = useState(null);
    const [number, setNumber] = useState("");
    // Number aane tak card me shimmer. Bina iske pehle render pe "No number
    // assigned yet" flash hota tha — jabki number bas load ho raha hota hai.
    const [numberLoading, setNumberLoading] = useState(true);
    // Dial ho chuka hai aur hum uski call ka intezaar kar rahe hain.
    const [awaitingCall, setAwaitingCall] = useState(false);
    // "Waiting for your call" modal — dialer tap pe khulta hai.
    const [showWaiting, setShowWaiting] = useState(false);
    // Copy tap ke baad ka transient "Copied" tooltip.
    const [copiedFlash, setCopiedFlash] = useState(false);
    // Gate ek hi baar chale — do timers ek saath navigate na kar dein.
    const routedRef = useRef(false);

    const agentId = sessionStorage.getItem("SelectAgentId");
    const agentBuisnesId = sessionStorage.getItem("SelectAgentBusinessId");

    const agentName = (
        agentDetails?.agentName ||
        agentData?.agent?.agentName ||
        "Sophia"
    ).trim();

    /*
     * Agent ka apna avatar. Pehle card me "/svg/receptionist_profile.png"
     * hardcoded tha, isliye har agent ek hi generic tasveer dikhata tha —
     * chahe uska avatar Female-01 ho ya Female-05.
     *
     * Wahi do sources jo agentName ke liye hain, usi tarteeb me: fresh
     * agentDetails pehle, phir store ka agentData.
     */
    const agentAvatarSrc =
        agentDetails?.avatar ||
        agentData?.agent?.avatar ||
        agentDetails?.agentAvatar ||
        agentData?.agent?.agentAvatar ||
        "/svg/receptionist_profile.png";

    const businessNameRaw =
        businessDetails?.businessName ||
        agentData?.businessName?.businessName ||
        agentData?.business?.businessName ||
        agentData?.agent?.businessName ||
        "your business";
    const businessName =
        typeof businessNameRaw === "string"
            ? businessNameRaw.substring(0, 40)
            : "your business";

    useEffect(() => {
        let cancelled = false;
        (async () => {
            // agentId hi nahi to fetch bemaani — par shimmer band karna zaroori
            // hai, warna wo hamesha ghoomta rehta.
            if (!agentId) {
                setNumberLoading(false);
                return;
            }
            try {
                const response = await fetchAgentDetailById({
                    agentId,
                    bussinesId: agentBuisnesId,
                });
                if (cancelled) return;
                const agentInfo = response?.data;
                setAgentDetails(agentInfo?.agent || null);
                setBusinessDetails(agentInfo?.business || agentInfo?.businessName || null);
                setNumber(parseVoipNumber(agentInfo?.agent?.voip_numbers));
            } catch (err) {
                console.error("[AgentLive] agent detail fetch failed:", err);
            } finally {
                if (!cancelled) setNumberLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [agentId, agentBuisnesId]);

    useEffect(() => {
        trackEvent("b_flow_agent_live_view", { screen: "agent_live" });
    }, []);

    /*
     * Confetti — sirf tab jab user "Continue with this number" se yahan aaya ho.
     * ReceptionistNumber navigate ke saath { celebrate: true } bhejta hai;
     * back / app-kill wapasi (BflowReturnGuard) me wo state nahi hoti, isliye
     * wahan confetti nahi chalti.
     *
     * canvas-confetti apna canvas khud inject karta hai, isliye yahan koi
     * <canvas> mount karne ki zarurat nahi. StrictMode dev me effect do baar
     * chalata hai — confettiFiredRef us case ko rokta hai.
     */
    const confettiFiredRef = useRef(false);
    useEffect(() => {
        if (confettiFiredRef.current) return;

        // Do raste — navigate state, aur ek one-shot sessionStorage key. Key
        // isliye ki kuch redirects (SecureRoute/guards) navigate state gira
        // dete hain; dono me se jo mile, celebration ek hi baar chalti hai.
        let flagged = false;
        try {
            flagged = sessionStorage.getItem(CELEBRATE_KEY) === "1";
        } catch {
            /* storage blocked */
        }
        if (!location.state?.celebrate && !flagged) return;
        confettiFiredRef.current = true;

        try {
            sessionStorage.removeItem(CELEBRATE_KEY);
        } catch {
            /* ignore */
        }
        // State ko history se hata do, warna refresh/back par dobara chal jaati.
        if (location.state?.celebrate) {
            navigate(location.pathname, { replace: true, state: null });
        }

        const palette = ["#6524EB", "#3DC322", "#FFC700", "#22C55E", "#FFFFFF"];
        // zIndex explicit — canvas-confetti ka default 100 hai, aur app ke kuch
        // fixed overlays usse upar baithte hain.
        const base = { colors: palette, ticks: 220, zIndex: 9999 };

        // Stage 1 — beech se ek chauda burst.
        confetti({
            ...base,
            particleCount: 90,
            spread: 80,
            startVelocity: 45,
            origin: { x: 0.5, y: 0.42 },
        });

        // Stage 2 — 250ms baad dono kinaron se, taaki layered lage.
        const t1 = setTimeout(() => {
            confetti({
                ...base,
                particleCount: 50,
                angle: 60,
                spread: 60,
                startVelocity: 48,
                origin: { x: 0, y: 0.65 },
            });
            confetti({
                ...base,
                particleCount: 50,
                angle: 120,
                spread: 60,
                startVelocity: 48,
                origin: { x: 1, y: 0.65 },
            });
        }, 250);

        // Stage 3 — halka sa upar se girta hua finale.
        const t2 = setTimeout(() => {
            confetti({
                ...base,
                particleCount: 40,
                spread: 100,
                startVelocity: 25,
                gravity: 0.9,
                scalar: 0.9,
                origin: { x: 0.5, y: 0.2 },
            });
        }, 550);

        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state]);

    /*
     * Back → /fast-agent-detail.
     *
     * Peeche /receptionist-number hai — number assign ho chuka hai, wahan wapas
     * jaana bemaani (aur wo screen dobara assign karne ki koshish karegi).
     */
    useBackTo("/fast-agent-detail");

    /*
     * Back dabna SOCH-SAMAJH KE liya gaya exit hai, app kill ho jaana nahi —
     * isliye gate ka nishaan saaf kar dete hain. Warna BflowReturnGuard agle
     * cold boot pe user ko yahin wapas kheench laata, jabki wo khud nikla tha.
     */
    useEffect(() => {
        const onPop = () => clearPendingDial();
        window.addEventListener("popstate", onPop);
        return () => window.removeEventListener("popstate", onPop);
    }, []);

    /*
     * Post-call gate yahin ARM hota hai — screen pe pahunchte hi, CTA tap pe
     * NAHI. Bahut users button nahi dabate; wo screen pe likha number padh kar
     * apne phone ke dialer se laga dete hain. Tap pe arm karte to un sabka gate
     * kabhi chalta hi nahi aur wo yahin atke reh jaate.
     *
     * Ab jo bhi phone call is agent pe is waqt ke BAAD aayegi, wo gate trigger
     * karegi — chaahe button se ho ya haath se dial ki gayi ho.
     */
    useEffect(() => {
        if (!agentId) return;
        armPostCallGate(agentId);

        /*
         * App-kill se laut kar aaye hain (BflowReturnGuard ne yahan bheja) —
         * modal dobara khol do.
         *
         * dialerOpened TRUE hai to user pehle hi dial kar chuka hai. Bina iske
         * screen bilkul waisi khulti hai jaise usne kuch kiya hi na ho, aur
         * usse pata hi nahi chalta ki app abhi bhi uski call ka intezaar kar
         * raha hai.
         *
         * Call already capture ho chuki ho to modal nahi — neeche wala effect
         * card dikha dega.
         */
        if (getPendingDial()?.dialerOpened) {
            setAwaitingCall(true);
            setShowWaiting(true);
        }
    }, [agentId]);

    /*
     * Aakhri screen ka context. Number assign ho chuka hai aur user ko ab apne
     * hi phone se call karna hai — agent ko yahi nudge karna hai. Number aane ka
     * wait karte hain taaki wo use bol sake; ek hi baar jaata hai.
     */
    useEffect(() => {
        if (introInjectedRef.current) return;
        if (callStatus !== "active") return;
        if (!number) return;
        introInjectedRef.current = true;
        injectContext(
            `CONTEXT: The customer has just reached the final "You're live" screen. Their business number ${number} is now assigned and active, and there is a green "Call ${agentName} Now" button on screen. Continue the SAME conversation naturally — do NOT restart, do NOT re-introduce yourself, do NOT repeat your greeting. In ONE short, warm, celebratory sentence, congratulate them that they are live and invite them to tap "Call ${agentName} Now" to call you from their own phone and hear it for real. Never pushy.`
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [callStatus, number]);

    /*
     * APP-KILL ka fallback — EK BAAR, mount pe. Koi polling nahi.
     *
     * Normal case socket sambhalta hai: backend ka call_analyzed webhook
     * "phone_call_completed" bhejta hai aur initNotificationSocket wahin se
     * navigate kar deta hai.
     *
     * Par agar user call ke doran ya uske baad app hi BAND kar de, to wo event
     * kisi tak pahunchta hi nahi. Wapas aane par nishaan localStorage me bacha
     * hota hai, to yahan ek lookup chala kar chhooti hui call pakad lete hain.
     *
     * Purani calls match na ho jaayein isliye sirf wahi call li jaati hai jo
     * dialedAt ke baad shuru hui AUR call_type "phone_call" ho (onboarding ka
     * web call warna match kar jaata).
     */
    useEffect(() => {
        const pending = getPendingDial();
        if (!pending) return;

        let cancelled = false;
        (async () => {
            try {
                const res = await getAgentCalls(pending.agentId);
                if (cancelled || routedRef.current) return;
                const call = findCallAfter(res?.calls || res?.data || [], pending.dialedAt);
                if (!call) return; // abhi call hui hi nahi — socket intezaar karega

                routedRef.current = true;
                clearPendingDial();
                const durationSec = Math.round(Number(call.duration_ms || 0) / 1000);
                trackEvent("b_flow_real_call_complete", { duration_sec: durationSec });

                // Lambi call → isi screen pe card dikhao (socket wala rasta bhi
                // yahi karta hai). Chhoti call → seedha trial screen.
                if (routeForCall(call) === "/call-summary") {
                    setCapturedCall({
                        callId: call.call_id,
                        agentId: pending.agentId,
                        duration_ms: call.duration_ms,
                        start_timestamp: call.start_timestamp,
                        caller_name: call.contact_name || call.custom_analysis_data?.name || null,
                        from_number: call.from_number || null,
                        reason:
                            call.custom_analysis_data?.reason ||
                            call.call_summary ||
                            null,
                    });
                } else {
                    navigate("/21-day-trial", { replace: true });
                }
            } catch (err) {
                // Lookup fail — socket wala rasta abhi bhi khula hai.
                console.error("[AgentLive] resume call lookup failed:", err);
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);

    // Call detect ho gayi → intezaar wala modal band. Ab screen khud hi
    // "Call captured" card dikhati hai.
    useEffect(() => {
        if (capturedCall) setShowWaiting(false);
    }, [capturedCall]);

    // Number copy — 1.8s ke liye "Copied" tooltip. Timer ref me rakha hai
    // taaki unmount pe (ya dobara tap pe) purana timeout saaf ho jaaye.
    const copyTimerRef = useRef(null);
    useEffect(() => () => clearTimeout(copyTimerRef.current), []);

    const handleCopyNumber = async () => {
        if (!number) return;
        try {
            await navigator.clipboard.writeText(number);
        } catch (err) {
            console.error("[AgentLive] copy failed:", err);
            return;
        }
        trackEvent("b_flow_number_copied", { screen: "agent_live" });
        clearTimeout(copyTimerRef.current);
        setCopiedFlash(true);
        copyTimerRef.current = setTimeout(() => setCopiedFlash(false), 1800);
    };

    // "Click for details" → poora call summary.
    const handleViewDetails = () => {
        trackEvent("b_flow_view_call_details", { screen: "agent_live" });
        navigate("/call-summary", {
            state: {
                callId: capturedCall?.callId,
                agentId: capturedCall?.agentId || agentId,
                call: capturedCall,
            },
        });
    };

    const handleActivate = () => {
        trackEvent("b_flow_activate_clicked", { screen: "agent_live" });
        navigate("/21-day-trial");
    };

    // Tap = user apne phone se agent ko call kar raha hai. Gate yahan arm NAHI
    // hota — wo screen pe aate hi ho chuka hai (upar wala effect), taaki haath
    // se dial karne wale users bhi cover hon. Yahan sirf analytics + setup score.
    // markSetupStep backend pe idempotent hai, to baad me aane wala real-call
    // webhook dobara fire kare to koi dikkat nahi.
    const handleDial = () => {
        trackEvent("b_flow_call_now_clicked", { screen: "agent_live" });
        /*
         * REMOVED: markSetupStep("firstRealCall", agentId)
         *
         * This credited the 5 points on a BUTTON TAP, not on a call. In the B
         * variant the forwarding flow lands on this screen, so tapping here
         * scored "first real call" without any phone call happening.
         *
         * The backend already credits this correctly and server-authoritatively
         * from Retell's call_ended webhook (agentController.js ~3957), gated on
         * call_type === "phone_call" AND inbound direction — and that path
         * survives the browser being refreshed or killed mid-call, which a
         * client mark does not. Marking here was a second, weaker definition of
         * "real call" competing with it.
         */
        // Sirf UI — gate isse nahi chalta, wo screen pe aate hi arm ho chuka hai.
        // Isliye haath se dial karne wale user ko bhi wahi treatment milta hai,
        // bas usse ye modal nahi dikhta.
        setAwaitingCall(true);
        setShowWaiting(true);
        // App kill hone par wapas laane ka haq sirf isi ke baad milta hai.
        markDialerOpened(agentId);
    };

    // CTA tap — pehle UI/analytics wala kaam (handleDial), phir dialer khol do.
    // Anchor ki jagah AnimatedButton hai, isliye navigation ab JS karta hai.
    const handleDialClick = () => {
        if (!number) return;
        handleDial();
        window.location.href = toTelHref(number);
    };

    // "Try again" — dialer dobara kholo. Anchor pe click karne ke bajaye seedha
    // tel: par bhejte hain; gate pehle se armed hai to kuch aur karne ki zarurat
    // nahi.
    const handleRetryDial = () => {
        if (!number) return;
        trackEvent("b_flow_call_retry_clicked", { screen: "agent_live" });
        markDialerOpened(agentId);
        window.location.href = toTelHref(number);
    };

    // "I'll do it later" — user abhi call nahi karna chahta. Usse yahan phansaye
    // rakhna galat hai (is screen pe koi doosra rasta hai hi nahi), to agle
    // funnel step pe bhej dete hain. Gate ka nishaan saaf kar dete hain warna wo
    // 15 min tak armed rehta aur baad me achanak fire ho sakta tha.
    const handleLater = () => {
        trackEvent("b_flow_call_later_clicked", { screen: "agent_live" });
        clearPendingDial();
        setShowWaiting(false);
        navigate("/21-day-trial");
    };

    // Status pills — teen chhote icons, har ek pe green check badge.
    // Icon + label + value sirf presentation hai; koi state isse nahi badalti.
    const statusItems = [
        {
            label: "Business Training",
            value: "Completed",
            icon: (
                <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="22" cy="22" r="22" fill="#F0EAFC" />
                    <path d="M21.9562 24.4911L29.4801 21.5276V27.9835C29.4801 28.1699 29.376 28.3428 29.2095 28.4262C26.9016 29.5818 24.3561 30.1835 21.7751 30.1835C19.1941 30.1835 16.6487 29.5818 14.3408 28.4262C14.2591 28.3847 14.1906 28.3213 14.1428 28.2432C14.0949 28.165 14.0697 28.0752 14.0699 27.9835V21.5273L21.5941 24.4911C21.7109 24.5365 21.8403 24.5365 21.9567 24.4911H21.9562ZM34.6704 18.0687L21.8469 13.0138C21.8008 12.9954 21.7494 12.9954 21.7034 13.0138L8.87985 18.0684C8.84329 18.0828 8.81191 18.1079 8.7898 18.1404C8.76769 18.1729 8.75586 18.2113 8.75586 18.2506C8.75586 18.2899 8.76769 18.3282 8.7898 18.3607C8.81191 18.3932 8.84329 18.4183 8.87985 18.4327L21.7034 23.4831C21.7494 23.5016 21.8008 23.5016 21.8469 23.4831L34.6702 18.4327C34.7067 18.4183 34.7381 18.3932 34.7602 18.3607C34.7823 18.3282 34.7941 18.2899 34.7941 18.2506C34.7941 18.2113 34.7823 18.1729 34.7602 18.1404C34.7381 18.1079 34.7067 18.0828 34.6702 18.0684M33.7511 27.8679V19.8478L32.7715 20.2349V27.8695C32.502 27.9528 32.2585 28.1041 32.0646 28.309C31.8707 28.514 31.733 28.7654 31.6648 29.0392C31.5966 29.3129 31.6002 29.5996 31.6753 29.8715C31.7504 30.1435 31.8944 30.3914 32.0934 30.5914C31.7874 30.8989 31.6157 31.3152 31.6158 31.7491V34.5094C31.6158 34.6394 31.6674 34.7641 31.7593 34.856C31.8512 34.9479 31.9758 34.9995 32.1058 34.9995H34.4213C34.5512 34.9994 34.6758 34.9478 34.7676 34.8559C34.8595 34.764 34.9111 34.6394 34.9111 34.5094V31.7491C34.9111 31.3152 34.7394 30.8989 34.4334 30.5914C34.6327 30.3912 34.7769 30.1428 34.852 29.8704C34.9271 29.5981 34.9304 29.3109 34.8618 29.0368C34.7931 28.7628 34.6548 28.5111 34.4602 28.3063C34.2656 28.1015 34.0213 27.9505 33.7511 27.8679Z" fill="#6524EB" />
                </svg>

            ),
        },
        {
            label: "Initial Web call",
            value: "Tested",
            icon: (
                <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="22" cy="22" r="22" fill="#F0EAFC" />
                    <path d="M20.2555 13.394C20.0833 12.3306 19.1736 11.4888 18.1048 11.5908L14.0673 11.9815C13.1097 12.0742 12.3792 12.8975 12.4996 13.8582C13.0497 18.3655 15.0931 22.5589 18.3039 25.7697C21.5147 28.9806 25.7082 31.0239 30.2155 31.574C31.1708 31.6891 31.9956 30.9601 32.0921 30.0063L32.4713 25.9589C32.5748 24.89 31.7268 23.9788 30.6688 23.8073C29.8862 23.6819 29.1174 23.482 28.3729 23.2103C27.5319 22.9053 26.5611 23.1223 26.036 23.8482L24.9656 25.3156C21.98 23.788 20.1976 22.0056 18.6701 19.02L20.1412 18.0199C20.8996 17.504 21.1406 16.5078 20.8301 15.6444C20.5697 14.9136 20.3773 14.1602 20.2555 13.394Z" fill="#6524EB" />
                </svg>

            ),
        },
        {
            label: "Business number",
            value: "Active",
            icon: (
                <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="22" cy="22" r="22" fill="#F0EAFC" />
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M31.8333 22.2013C31.9308 22.2638 31.9658 22.2655 31.9858 22.288C31.9942 22.2988 32 22.3113 32 22.3255V29.543C32 29.985 31.8244 30.4089 31.5118 30.7215C31.1993 31.034 30.7754 31.2096 30.3333 31.2096H13.6667C13.2246 31.2096 12.8007 31.034 12.4882 30.7215C12.1756 30.4089 12 29.985 12 29.543V22.4221C11.9999 22.3904 12.0089 22.3594 12.0259 22.3326C12.0429 22.3058 12.0671 22.2845 12.0958 22.271C12.1245 22.2575 12.1565 22.2525 12.1879 22.2566C12.2193 22.2606 12.249 22.2735 12.2733 22.2938C12.7383 22.6821 13.2025 22.8763 13.6667 22.8763H19.9167V23.7096C19.9167 23.9137 19.9916 24.1107 20.1273 24.2633C20.2629 24.4158 20.4498 24.5133 20.6525 24.5371L20.75 24.543H23.25C23.471 24.543 23.683 24.4552 23.8393 24.2989C23.9955 24.1426 24.0833 23.9306 24.0833 23.7096V22.8763H30.3333C30.8333 22.8763 31.3333 22.6513 31.8333 22.2013ZM25.3333 12.043C25.7754 12.043 26.1993 12.2186 26.5118 12.5311C26.8244 12.8437 27 13.2676 27 13.7096V16.2096H30.3333C30.7754 16.2096 31.1993 16.3852 31.5118 16.6978C31.8244 17.0104 32 17.4343 32 17.8763V20.3763C32 20.8183 31.8244 21.2423 31.5118 21.5548C31.1993 21.8674 30.7754 22.043 30.3333 22.043H24.0833V21.2096C24.0833 21.0055 24.0084 20.8085 23.8727 20.656C23.7371 20.5035 23.5502 20.406 23.3475 20.3821L23.25 20.3763H20.75C20.529 20.3763 20.317 20.4641 20.1607 20.6204C20.0045 20.7767 19.9167 20.9886 19.9167 21.2096V22.043H13.6667C13.2246 22.043 12.8007 21.8674 12.4882 21.5548C12.1756 21.2423 12 20.8183 12 20.3763V17.8763C12 17.4343 12.1756 17.0104 12.4882 16.6978C12.8007 16.3852 13.2246 16.2096 13.6667 16.2096H17V13.7096C17 13.2676 17.1756 12.8437 17.4882 12.5311C17.8007 12.2186 18.2246 12.043 18.6667 12.043H25.3333ZM25.3333 14.543H18.6667V16.2096H25.3333V14.543Z" fill="#6524EB" />
                </svg>

            ),
        },
    ];

    // Har row ka apna icon hai. Pehli line me business ka naam aata hai —
    // lamba hua to CSS use ek line me ellipsis kar deta hai.
    const tryItems = [
        {
            text: `Ask about services by ${businessName}`,
            icon: (
                <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10.5" cy="10.5" r="10.5" fill="#6524EB" />
                    <path d="M5.4612 7.72873C5.4612 8.75602 6.32594 9.59069 7.39024 9.59069C8.45455 9.59069 9.31929 8.75602 9.31929 7.72873C9.31929 6.70144 8.45455 5.86677 7.39024 5.86677C6.32594 5.86677 5.4612 6.70144 5.4612 7.72873ZM8.28825 7.72873C8.28825 8.21027 7.88914 8.59551 7.39024 8.59551C6.89135 8.59551 6.49224 8.21027 6.49224 7.72873C6.49224 7.24719 6.89135 6.86196 7.39024 6.86196C7.88914 6.86196 8.28825 7.24719 8.28825 7.72873ZM11.9634 6.57303C11.9634 7.43981 12.6951 8.14607 13.5931 8.14607C14.4911 8.14607 15.2228 7.43981 15.2228 6.57303C15.2228 5.70626 14.4911 5 13.5931 5C12.6951 5 11.9634 5.70626 11.9634 6.57303ZM14.1918 6.57303C14.1918 6.89406 13.9257 7.15088 13.5931 7.15088C13.2605 7.15088 12.9945 6.89406 12.9945 6.57303C12.9945 6.25201 13.2605 5.99518 13.5931 5.99518C13.9257 5.99518 14.1918 6.25201 14.1918 6.57303ZM17.5177 11.3082H16.3038C15.9213 8.78812 14.4579 8.40289 13.5931 8.40289C12.7284 8.40289 11.2816 8.78812 10.8825 11.3082H9.90133C9.20288 10.1685 8.23836 9.86356 7.39024 9.86356C6.54213 9.86356 5.56098 10.1685 4.87916 11.3082H3.51552C3.23282 11.3082 3 11.5329 3 11.8058C3 12.0787 3.23282 12.3034 3.51552 12.3034H4.41353C4.21397 12.8812 4.08093 13.5875 3.99778 14.4382C3.98115 14.7111 4.18071 14.9518 4.46341 14.9839C4.74612 15.0161 4.99557 14.8074 5.02882 14.5345C5.24501 12.0947 6.02661 10.8587 7.37361 10.8587C8.72062 10.8587 9.51885 12.0947 9.7184 14.5345C9.73503 14.7913 9.96785 15 10.2339 15C10.2506 15 10.2672 15 10.2838 15C10.5665 14.9839 10.7827 14.7432 10.7494 14.4543C10.6829 13.5875 10.5333 12.8973 10.3337 12.3194H17.4845C17.7672 12.3194 18 12.0947 18 11.8218C18 11.549 17.8004 11.3082 17.5177 11.3082ZM11.9302 11.3082C12.163 10.0401 12.7118 9.39807 13.5931 9.39807C14.4745 9.39807 15.0233 10.0401 15.2561 11.3082H11.9302Z" fill="white" />
                </svg>


            ),
        },
        {
            text: "Try booking an appointment",
            icon: (
                <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10.5" cy="10.5" r="10.5" fill="#6524EB" />
                    <path d="M9.47691 12.3227C9.61809 12.4664 9.79917 12.5703 9.9956 12.5946C10.2649 12.6289 10.5258 12.5367 10.7122 12.3469L12.741 10.2814C12.9098 10.1096 12.8921 9.81976 12.6895 9.67133C12.5307 9.55571 12.3082 9.5893 12.1693 9.73148L10.1459 11.7915C10.1244 11.8134 10.0868 11.8134 10.0654 11.7915L9.00878 10.7158C8.93205 10.6377 8.8323 10.6002 8.73178 10.6002C8.62205 10.6002 8.51156 10.6463 8.4333 10.7401C8.29749 10.9018 8.32358 11.1502 8.47244 11.3002L9.47837 12.3243L9.47691 12.3227Z" fill="white" stroke="white" stroke-width="0.4" />
                    <path d="M7.35714 4C7.14076 4 6.96429 4.17968 6.96429 4.39997V5.7741L6.63588 5.83035C5.6875 5.9944 5 6.82246 5 7.80054V14.0001C5 15.1024 5.88163 16 6.96429 16H14.0357C15.1184 16 16 15.1024 16 14.0001V7.80054C16 6.82248 15.3125 5.99442 14.3641 5.83035L14.0357 5.7741V4.39997C14.0357 4.17967 13.8592 4 13.6429 4C13.4265 4 13.25 4.17968 13.25 4.39997V5.79988H10.8929V4.39997C10.8929 4.17967 10.7164 4 10.5 4C10.2836 4 10.1071 4.17968 10.1071 4.39997V5.79988H7.75V4.39997C7.75 4.17967 7.57352 4 7.35714 4ZM14.0357 6.59983C14.6856 6.59983 15.2143 7.13807 15.2143 7.79975V13.9993C15.2143 14.661 14.6856 15.1993 14.0357 15.1993H6.96429C6.31438 15.1993 5.78571 14.661 5.78571 13.9993V7.79975C5.78571 7.13807 6.31438 6.59983 6.96429 6.59983H14.0357Z" fill="white" stroke="white" stroke-width="0.4" />
                </svg>

            ),
        },
        {
            text: "Ask a common customer question",
            icon: (
                <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10.5" cy="10.5" r="10.5" fill="#6524EB" />
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M10.8686 15.8223C10.8686 16.1345 10.7646 16.4121 10.5564 16.6376C10.3483 16.8804 10.1054 17.0018 9.79321 17.0018C9.48099 17.0018 9.2208 16.8804 9.03 16.6376C8.82185 16.4121 8.71777 16.1345 8.71777 15.8223C8.71777 15.5101 8.82185 15.2326 9.01265 15.0071C9.2208 14.7816 9.48099 14.6602 9.79321 14.6602C10.1054 14.6602 10.3656 14.7816 10.5738 15.0071C10.7646 15.2326 10.8686 15.5101 10.8686 15.8223Z" fill="white" />
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M13.2792 8.17427C13.2792 8.98952 13.0363 9.71804 12.5506 10.3425C12.4812 10.4119 12.0303 10.8802 11.163 11.7301C10.6426 12.2332 10.3651 12.7188 10.313 13.2045C10.313 13.3259 10.2957 13.4474 10.2783 13.5688C10.261 13.6729 10.1743 13.7422 10.0355 13.7943C9.87939 13.8463 9.75797 13.881 9.67124 13.881C9.37636 13.881 9.20291 13.8637 9.15087 13.8463C9.02945 13.7943 8.96007 13.6729 8.96007 13.4474C8.96007 12.7709 9.06414 12.2332 9.30698 11.8342C9.67124 11.3485 10.0355 10.8629 10.3998 10.3772C10.8508 9.78742 11.0589 9.23236 11.0589 8.69464C11.0589 8.17427 10.9201 7.75797 10.6079 7.4284C10.313 7.11618 9.89674 6.96007 9.39371 6.96007C8.96007 6.96007 8.50908 7.09883 7.98871 7.39371C7.48568 7.68859 7.24284 7.82735 7.26019 7.82735C7.08673 7.82735 7 7.28964 7 6.2142C7 6.12747 7.08673 6.04074 7.26019 5.91932C8.14482 5.31222 9.06414 5 10.0355 5C10.9895 5 11.7701 5.29488 12.3772 5.88463C12.9669 6.45704 13.2792 7.2376 13.2792 8.17427Z" fill="white" />
                </svg>

            ),
        },
    ];

    // Status pill ke icon pe baithne wala chhota green tick.
    const Check = () => (
        <svg width="9" height="9" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
                d="M3.5 8L6.5 11L12.5 5"
                stroke="#fff"
                strokeWidth="2.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );

    return (
        <div className={styles.containerBox}>
            {/* ── Hero: agent_live_graphic background + "You did it!" ────── */}
            <div className={styles.hero}>
                <span className={styles.heroBadge}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path
                            d="M5 12.5L9.8 17.2L19.2 7.2"
                            stroke="#6425E8"
                            strokeWidth="3.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </span>

                <h2 className={styles.heroTitle}>Congratulation!</h2>
                <p className={styles.heroSub}>
                    Your AI receptionist is all set and ready to{" "}
                    <span className={styles.heroSubAccent}>help your business grow.</span>
                </p>
            </div>

            <div className={styles.container}>
                {/* ── Agent card: avatar + number + status pills ──────────── */}
                <div className={styles.agentCard}>
                    <div className={styles.agentHead}>
                        <div className={styles.avatarWrap}>
                            <img
                                src={agentAvatarSrc}
                                alt=""
                                className={styles.avatar}
                                aria-hidden="true"
                                onError={(e) => {
                                    // Avatar file na mile to generic tasveer —
                                    // card me toota image icon nahi dikhna chahiye.
                                    e.currentTarget.src = "/svg/receptionist_profile.png";
                                }}
                            />
                            <span className={styles.avatarDot} aria-hidden="true" />
                        </div>

                        {/* Naam, sub-line aur number pill — teeno avatar ke
                            bagal wale isi column me baithte hain. */}
                        <div className={styles.agentHeadText}>
                            <p className={styles.meetLine}>
                                Meet <span className={styles.meetName}>{agentName}</span>
                            </p>
                            <p className={styles.meetSub}>Your AI receptionist</p>

                            <div className={styles.numberCard}>
                                <div className={styles.numberCardText}>
                                    {numberLoading ? (
                                        <>
                                            <span className={`${styles.skeleton} ${styles.skeletonLabel}`} />
                                            <span className={`${styles.skeleton} ${styles.skeletonNumber}`} />
                                        </>
                                    ) : (
                                        <>
                                            <span className={styles.numberLabel}>Number Assigned</span>
                                            {number ? (
                                                <span className={styles.number}>{formatNumberForDisplay(number)}</span>
                                            ) : (
                                                <span className={`${styles.number} ${styles.numberUnavailable}`}>
                                                    No number assigned yet
                                                </span>
                                            )}
                                        </>
                                    )}
                                </div>

                                {/* Copy — number aane par hi dikhta hai. Tap pe 1.8s
                                    ke liye "Copied" tooltip. */}
                                {!numberLoading && number && (
                                    <button
                                        type="button"
                                        className={styles.copyBtn}
                                        onClick={handleCopyNumber}
                                        aria-label="Copy number"
                                    >
                                        <img src="/svg/copy_icon_4.svg" alt="" aria-hidden="true" />
                                        {copiedFlash && (
                                            <span className={styles.copyTooltip}>Copied</span>
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    <ul className={styles.statusList}>
                        {statusItems.map((item) => (
                            <li key={item.label} className={styles.statusItem}>
                                <span className={styles.statusIcon}>
                                    {item.icon}
                                    <span className={styles.statusCheck}>
                                        <Check />
                                    </span>
                                </span>
                                <span className={styles.statusLabel}>{item.label}</span>
                                <span className={styles.statusValue}>{item.value}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Call detect hone se PEHLE "Test like you are a customer", BAAD me
                    uski jagah "Call details captured" card. Dono ek saath dikhane ka
                    matlab nahi — call ho chuki hai to ab kya try karna hai wo bemaani. */}
                {capturedCall ? (
                    // Poora card hi tap target hai — sirf arrow tile pe tap karna
                    // phone pe bahut chhota nishana tha. Arrow ab sirf affordance
                    // hai (span), warna button-in-button invalid HTML ho jaata.
                    <button
                        type="button"
                        className={styles.capturedCard}
                        onClick={handleViewDetails}
                    >
                
                        <div className={styles.capturedMain}>
                            <p className={styles.capturedTitle}>Call details captured</p>

                            <div className={styles.capturedRow}>
                                <span className={styles.capturedIcon} aria-hidden="true">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="8" r="3.6" stroke="#fff" strokeWidth="2" />
                                        <path
                                            d="M5 19c0-3.3 3.1-5.4 7-5.4s7 2.1 7 5.4"
                                            stroke="#fff"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                </span>
                                <span className={styles.capturedText}>
                                    {capturedCall.caller_name || "Unknown caller"}
                                    {capturedCall.from_number ? ` (${capturedCall.from_number})` : ""}
                                </span>
                            </div>

                            {capturedCall.reason && (
                                <div className={styles.capturedRow}>
                                    <span className={styles.capturedIcon} aria-hidden="true">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                            <path
                                                d="M6.5 3.5h3l1.5 4-2 1.5a12 12 0 006 6l1.5-2 4 1.5v3a2 2 0 01-2.2 2A17 17 0 014.5 5.7 2 2 0 016.5 3.5z"
                                                stroke="#fff"
                                                strokeWidth="1.9"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </span>
                                    <span className={styles.capturedText}>{capturedCall.reason}</span>
                                </div>
                            )}
                        </div>

                        {/* Wahi "details" wala rasta — ab side me arrow tile ban gaya. */}
                        <span
                            className={styles.capturedArrow}
                            aria-hidden="true"
                        >
                            <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M8.20313 15.75L6.39063 13.9531L11.1484 9.19531L0 9.19531L0 6.55469L11.1484 6.55469L6.39063 1.80469L8.20313 -2.38419e-07L16.0781 7.875L8.20313 15.75Z" fill="#5F33E1" />
                            </svg>

                        </span>
                    </button>
                ) : (
                    <div className={styles.tryCard}>
                        {/* Naam hardcoded "Sophia" tha — neeche wala button
                            pehle se {agentName} use karta hai, to ek hi card me
                            do alag naam dikhte the. */}
                        <h4 className={styles.tryTitle}>Call {agentName} &amp; Test like a customer</h4>
                        <ul className={styles.tryList}>
                            {tryItems.map((item) => (
                                <li key={item.text} className={styles.tryItem}>
                                    <span className={styles.tryCheck}>{item.icon}</span>
                                    <span className={styles.tryText}>{item.text}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            <div className={styles.footer}>
                {capturedCall ? (
                    // Call ho chuki — ab agla kaam Activate hai, dobara call karna nahi.

                    <AnimatedButton
                        label={`Activate ${agentName} ->`}
                        onClick={handleActivate}
                        positionType="relative"
                        width="100%"
                        bottom="0"
                        size="20px"
                    />
                ) : (
                    <>
                        {/* Handwritten nudge — sirf sajavat, CTA ke thoda upar. */}
                        <div className={styles.tryItOut} aria-hidden="true">
                            <span className={styles.tryItOutText}>Try it out!</span>
                            {/* Dono path stroke-dash se "bante hue" dikhte hain —
                                pehle curve, phir uske sire pe arrowhead. */}
                            <svg className={styles.tryItOutArrow} viewBox="0 0 46 30" fill="none">
                                <path
                                    className={styles.tryItOutArrowLine}
                                    d="M2 3c12 0 24 6 32 20"
                                    stroke="#24252c"
                                    strokeWidth="1.6"
                                    strokeLinecap="round"
                                />
                                <path
                                    className={styles.tryItOutArrowHead}
                                    d="M27 22.5l7.5 1.5.5-7.5"
                                    stroke="#24252c"
                                    strokeWidth="1.6"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>

                        {/* Shared AnimatedButton. Dialer ab JS se khulta hai
                            (window.location.href) — wahi tareeka jo "Try again" use
                            karta hai, kyunki component anchor nahi render karta. */}
                        <AnimatedButton
                            label={
                                <span className={styles.dialBtnLabel}>
                                    Call {agentName} Now
                                </span>
                            }
                            onClick={handleDialClick}
                            disabled={!number}
                            positionType="relative"
                            width="100%"
                            bottom="0"
                            bgSrc="svg/svg-theme-green.svg"
                            size="20px"
                        />
                    </>
                )}
                <p className={styles.footerNote}>
                    {capturedCall ? (
                        <>
                            <strong className={styles.footerNoteName}>{agentName}</strong>{" "}
                            handled your call and captured the important details automatically.
                        </>
                    ) : awaitingCall ? (
                        <>
                            Waiting for your call to finish — we'll show you what{" "}
                            <strong className={styles.footerNoteName}>{agentName}</strong>{" "}
                            captured.
                        </>
                    ) : (
                        <>
                            Give <strong className={styles.footerNoteName}>{agentName}</strong>{" "}
                            a real call and experience how she'll handle your customers.
                        </>
                    )}
                </p>
            </div>

            <WaitingForCallModal
                isOpen={showWaiting}
                agentName={agentName}
                callInProgress={callInProgress}
                onLater={handleLater}
                onRetry={handleRetryDial}
            />
        </div>
    );
};

export default AgentLive;
